
import { PersonalityResult, QuizAnswer } from '@/types/quiz';

/**
 * Calculates personality results based on quiz answers
 */
export const calculateResults = (userAnswers: QuizAnswer[]): PersonalityResult => {
  // Group answers by category
  const answersByCategory = userAnswers.reduce((acc, answer) => {
    if (!acc[answer.category]) {
      acc[answer.category] = [];
    }
    acc[answer.category].push(answer.value);
    return acc;
  }, {} as Record<string, number[]>);

  // Calculate average score for each category
  const categoryScores = Object.entries(answersByCategory).reduce((acc, [category, values]) => {
    const sum = values.reduce((a, b) => a + b, 0);
    const average = values.length > 0 ? sum / values.length : 3; // Default to middle value if no answers
    acc[category] = average;
    return acc;
  }, {} as Record<string, number>);

  // Determine personality type based on scores
  const personalityScore = categoryScores.personality || 3;
  const personalityStrength = personalityScore > 3 ? 'Extroverted' : 'Introverted';
  const personalityLevel = 
    personalityScore > 4 ? 'Very High' : 
    personalityScore > 3 ? 'High' : 
    personalityScore > 2 ? 'Moderate' : 'Low';

  // Determine fear level
  const fearScore = categoryScores.fears || 2;
  const fearLevel = fearScore > 3 ? 'High' : 'Low';

  // Determine relationship patterns
  const relationshipScore = categoryScores.relationships || 3;
  const relationshipLevel = relationshipScore > 3 ? 'Trusting' : 'Cautious';

  // Determine health status
  const healthScore = categoryScores.health || 3;
  const healthStatus = healthScore > 3 ? 'Good' : 'Needs Attention';

  // Determine SDOH barriers
  const sdohScore = categoryScores.sdoh || 3;
  const sdohBarriers = sdohScore > 3 ? 'Low' : 'Moderate';

  return {
    personality: {
      score: personalityScore,
      strength: personalityStrength,
      level: personalityLevel
    },
    fears: {
      score: fearScore,
      level: fearLevel
    },
    relationships: {
      score: relationshipScore,
      level: relationshipLevel
    },
    health: {
      score: healthScore,
      status: healthStatus
    },
    sdoh: {
      score: sdohScore,
      barriers: sdohBarriers
    }
  };
};

/**
 * Saves quiz results to Supabase
 */
export const saveResultsToSupabase = async (userId: string, results: PersonalityResult) => {
  try {
    console.log("Saving results to Supabase for user:", userId, results);
    
    // Create a user_assessment_results table in Supabase before uncommenting this
    // await supabase
    //   .from('user_assessment_results')
    //   .upsert({
    //     user_id: userId,
    //     results: results,
    //     created_at: new Date()
    //   });
  } catch (error) {
    console.error("Error saving results to Supabase:", error);
  }
};
